import React, { useState, useEffect } from 'react';
import { adminAPI, pickupEventsAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { showToast } from '../components/Toast';
import './Dashboard.css';

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [upcomingPickups, setUpcomingPickups] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
    loadPendingOrders();
    loadUpcomingPickups();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await adminAPI.getDashboard();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingOrders = async () => {
    try {
      const response = await adminAPI.getPendingOrders();
      setPendingOrders(response.data);
    } catch (error) {
      console.error('Error loading pending orders:', error);
    }
  };

  const loadUpcomingPickups = async () => {
    try {
      const response = await pickupEventsAPI.getUpcoming();
      setUpcomingPickups(response.data);
    } catch (error) {
      console.error('Error loading upcoming pickups:', error);
    }
  };

  const handleCreateDelivery = async (orderId) => {
    try {
      await adminAPI.createDelivery(orderId);
      loadPendingOrders();
      loadDashboard();
      showToast('Delivery created successfully', 'success');
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Error creating delivery';
      showToast(errorMessage, 'error');
    }
  };

  const handleViewDelivery = (deliveryId) => {
    navigate(`/deliveries/${deliveryId}`);
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="page dashboard-page">
      <h1 className="page-title">Admin Dashboard</h1>

      <div className="stats-grid">
        <div className="stat-card clickable" onClick={() => navigate('/admin/drivers')}>
          <h3>Total Drivers</h3>
          <div className="stat-number">{stats?.users?.total_drivers || 0}</div>
          <div className="stat-link">View all →</div>
        </div>

        <div className="stat-card clickable" onClick={() => navigate('/orders')}>
          <h3>Pending Orders</h3>
          <div className="stat-number">{stats?.orders?.pending || 0}</div>
          <div className="stat-link">View all orders →</div>
        </div>

        <div className="stat-card clickable" onClick={() => navigate('/admin/customers')}>
          <h3>Active Subscriptions</h3>
          <div className="stat-number">{stats?.subscriptions?.active || 0}</div>
          <div className="stat-link">View customers →</div>
        </div>
      </div>

      <div className="dashboard-grid-2col">
        <div className="card">
          <div className="card-header">
            <h2>Upcoming Pickup Events</h2>
            <button
              onClick={() => navigate('/admin/pickup-events')}
              className="btn btn-secondary btn-sm"
            >
              Manage All
            </button>
          </div>
          {upcomingPickups.length === 0 ? (
            <p>No upcoming pickup events</p>
          ) : (
            <ul className="pickup-list">
              {upcomingPickups.map((pickup) => (
                <li key={pickup.id} className="pickup-item">
                  <div className="pickup-info">
                    <strong>{new Date(pickup.scheduled_datetime).toLocaleString()}</strong>
                    <div className="pickup-details">
                      <span>{pickup.location}</span>
                      <span className="pickup-code">Code: {pickup.pickup_code}</span>
                    </div>
                    <div className="pickup-stats">
                      <span>{pickup.attendances?.length || 0} drivers</span>
                      <span>•</span>
                      <span>{pickup.deliveries?.length || 0} deliveries</span>
                      {pickup.admin_confirmed && <span className="confirmed-badge">✓ Confirmed</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/admin/pickup-events/${pickup.id}`)}
                    className="btn btn-primary btn-sm"
                  >
                    View
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card">
          <h2>Pending Orders</h2>
          {pendingOrders.length === 0 ? (
            <p>No pending orders</p>
          ) : (
            <ul className="order-list">
              {pendingOrders.map((order) => (
                <li key={order.id} className="order-item">
                  <div>
                    <strong>{order.order_number}</strong>
                    <br />
                    Customer ID: {order.customer_id}
                    <br />
                    Total: ${order.total_amount}
                    <br />
                    Address: {order.delivery_address}, {order.delivery_city}
                  </div>
                  {order.has_delivery ? (
                    <button
                      onClick={() => handleViewDelivery(order.delivery_id)}
                      className="btn"
                      style={{
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none'
                      }}
                    >
                      View Delivery
                    </button>
                  ) : (
                    <button
                      onClick={() => handleCreateDelivery(order.id)}
                      className="btn"
                      style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none'
                      }}
                    >
                      Create Delivery
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
